import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Save, X, Copy, Send, MessageSquare } from 'lucide-react';
import { createDispatchWithOverride } from '../../utils/dispatchAutoBlock';

// Helper function to format date in local timezone (YYYY-MM-DD)
const formatLocalDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to parse date string and extract local date
const parseLocalDate = (dateStr) => {
  if (!dateStr) return '';
  // If it's already YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  // If it's an ISO string with time, extract the date part using local timezone
  const d = new Date(dateStr);
  return formatLocalDate(d);
};

const STATUS_OPTIONS = [
  'Empty in Customer', 'Available for Pickup', 'Need Appt', 'On the way to Customer',
  'Important Customer', 'Delivered to Customer', 'In Yard', 'Container Returned',
  'On the way to Return', 'Delivered to Exam Location', 'New Shipment',
  'Empty Container in Yard', 'On the way to Pickup', 'Shipment Closed', 'Trouble'
];

// Task types for dispatch
const TASK_TYPES = [
  { value: 'LOAD_OUT_DELIVER', label: 'Load out from terminal and deliver' },
  { value: 'EMPTY_OUT_DELIVER', label: 'Empty out from terminal' },
  { value: 'RETURN_EMPTY', label: 'Return empty from/to' },
  { value: 'RETURN_LOAD', label: 'Return load from/to' },
  { value: 'DELIVER_FROM_TO', label: 'Deliver from/to' }
];

// Time options in 12-hour format
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    const hour24 = h.toString().padStart(2, '0');
    const min = m.toString().padStart(2, '0');
    const value = `${hour24}:${min}`;
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h < 12 ? 'AM' : 'PM';
    const label = `${hour12}:${min} ${ampm}`;
    TIME_OPTIONS.push({ value, label });
  }
}

export const DispatchModal = ({ 
  open, 
  onOpenChange, 
  shipment, 
  drivers, 
  terminals = [], 
  yards = [], 
  customers = [],
  onSuccess 
}) => {
  const { api } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('schedule');
  
  // Form state
  const [formData, setFormData] = useState({
    driver_id: '',
    phone_number: '',
    task_type: '',
    // New simplified location fields (format: "type:id:displayName")
    location_from: '',
    location_to: '',
    // New two-step location fields
    location_from_type: '',
    location_from_value: '',
    location_from_other: '',
    location_to_type: '',
    location_to_value: '',
    location_to_other: '',
    // Legacy fields for backward compatibility
    deliver_to_type: '', // customer_address, yard, other
    deliver_to_location: '',
    deliver_to_other: '',
    return_from_type: '', // yard, customer_address, other
    return_from_location: '',
    return_from_other: '',
    return_to_type: '', // yard, customer_address, other
    return_to_location: '',
    return_to_other: '',
    is_load_or_empty: '', // for DELIVER_FROM_TO task
    delivery_type: '', // LIVE_UNLOAD or DROP
    dispatch_date: formatLocalDate(new Date()),  // Default to today in local timezone
    dispatch_time_from: '',
    dispatch_time_to: '',
    require_signature: false,
    require_seal_number: false,
    require_chassis_number: false,
    require_container_picture: false,
    require_terminal_ticket: false,
    status_when_accepted: '',
    status_when_declined: '',
    status_when_started: '',
    status_when_done: '',
    customer_hours_from: '',
    customer_hours_to: '',
    customer_remark: ''
  });

  // Generated dispatch message
  const [dispatchMessage, setDispatchMessage] = useState('');

  // Initialize form with shipment data.
  // Depend on shipment?.id (not the object reference) so unrelated parent
  // re-renders (e.g., WebSocket list refreshes) don't clobber in-progress
  // dispatch edits.
  useEffect(() => {
    if (shipment && open) {
      // Hard-reset the entire form on every open so the previous dispatch
      // (date, driver, task, location, etc.) never leaks into the next one.
      // The dispatcher must consciously pick fields for each new dispatch.
      setFormData({
        driver_id: shipment.driver_id || '',
        phone_number: '',
        task_type: '',
        location_from: '',
        location_to: '',
        location_from_type: '',
        location_from_value: '',
        location_from_other: '',
        location_to_type: '',
        location_to_value: '',
        location_to_other: '',
        deliver_to_type: '',
        deliver_to_location: '',
        deliver_to_other: '',
        return_from_type: '',
        return_from_location: '',
        return_from_other: '',
        return_to_type: '',
        return_to_location: '',
        return_to_other: '',
        is_load_or_empty: '',
        delivery_type: '',
        dispatch_date: formatLocalDate(new Date()),
        dispatch_time_from: '',
        dispatch_time_to: '',
        require_signature: false,
        require_seal_number: false,
        require_chassis_number: false,
        require_container_picture: false,
        require_terminal_ticket: false,
        status_when_accepted: '',
        status_when_declined: '',
        status_when_started: '',
        status_when_done: '',
        customer_hours_from: '',
        customer_hours_to: '',
        customer_remark: ''
      });
      setDispatchMessage('');
    }
  }, [shipment?.id, open]);

  // Generate dispatch message whenever task/location changes
  useEffect(() => {
    if (!shipment || !formData.task_type) {
      setDispatchMessage('');
      return;
    }
    
    const msg = generateDispatchMessage();
    setDispatchMessage(msg);
  }, [formData.task_type, formData.location_from, formData.location_to,
      formData.is_load_or_empty, formData.delivery_type,
      formData.dispatch_date, formData.dispatch_time_from, formData.dispatch_time_to,
      formData.require_signature, formData.require_chassis_number,
      formData.require_seal_number, formData.require_container_picture,
      formData.require_terminal_ticket,
      formData.customer_hours_from, formData.customer_hours_to, formData.customer_remark,
      shipment]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-fill phone when driver is selected
    if (field === 'driver_id' && value) {
      const driver = drivers?.find(d => d.id === value);
      if (driver?.phone) {
        setFormData(prev => ({ ...prev, phone_number: driver.phone }));
      }
    }
    
    // Reset dependent fields when task type changes
    if (field === 'task_type') {
      const updates = {
        location_from: '',
        location_to: '',
        location_from_type: '',
        location_from_value: '',
        location_from_other: '',
        location_to_type: '',
        location_to_value: '',
        location_to_other: '',
        deliver_to_type: '',
        deliver_to_location: '',
        deliver_to_other: '',
        return_from_type: '',
        return_from_location: '',
        return_from_other: '',
        return_to_type: '',
        return_to_location: '',
        return_to_other: '',
        is_load_or_empty: ''
      };
      
      // For LOAD_OUT_DELIVER, auto-fill with appointment time from the shipment
      if (value === 'LOAD_OUT_DELIVER' && shipment) {
        updates.dispatch_date = shipment.appointment_date ? parseLocalDate(shipment.appointment_date) : '';
        updates.dispatch_time_from = shipment.appointment_time_from || '';
        updates.dispatch_time_to = shipment.appointment_time_to || '';
      }
      
      setFormData(prev => ({ ...prev, ...updates }));
    }
  };

  // Get terminal name by ID
  const getTerminalName = (id) => {
    const terminal = terminals?.find(t => t.id === id);
    return terminal?.name || terminal?.code || '';
  };

  // Get yard name by ID
  const getYardName = (id) => {
    const yard = yards?.find(y => y.id === id);
    return yard?.name || yard?.code || '';
  };

  // iter 264d.14 — resolve the dispatch label (what appears in the driver
  // dispatch message). Priority:
  //   1. If the shipment resolves to a specific saved customer address
  //      (matched on street + zip), use that address's `label`
  //      ("Main Warehouse", "Distribution Center", etc.) — this is the
  //      warehouse name the user set when creating the address.
  //   2. Fall back to the customer entity's `.name` ("Steam Logistics").
  //   3. Fall back to shipment.customer_name.
  //   4. Last resort: "Customer".
  const getDispatchLabelName = () => {
    if (shipment?.customer_id && customers?.length) {
      const cust = customers.find(c => c.id === shipment.customer_id);
      if (cust?.addresses?.length) {
        // Try to match by street+zip (more forgiving than pure equality).
        const shipStreet = (shipment.delivery_address || '').trim().toLowerCase();
        const shipZip = (shipment.delivery_zip || '').trim();
        const match = cust.addresses.find(a =>
          (a.address || '').trim().toLowerCase() === shipStreet &&
          (a.zip_code || '').trim() === shipZip
        ) || cust.addresses.find(a => a.is_default) || cust.addresses[0];
        if (match?.label) return match.label;
      }
      if (cust?.name) return cust.name;
    }
    return shipment?.customer_name || 'Customer';
  };

  // Get customer address
  const getCustomerAddress = () => {
    if (!shipment) return '';
    // First try shipment delivery fields
    const shipmentParts = [shipment.delivery_address, shipment.delivery_city, shipment.delivery_state, shipment.delivery_zip].filter(Boolean);
    if (shipmentParts.length > 1) return shipmentParts.join(', ');
    
    // Fallback: try customer entity's addresses array (default or first)
    if (shipment.customer_id && customers?.length) {
      const cust = customers.find(c => c.id === shipment.customer_id);
      if (cust?.addresses?.length) {
        const addr = cust.addresses.find(a => a.is_default) || cust.addresses[0];
        const custParts = [addr.address, addr.city, addr.state, addr.zip_code].filter(Boolean);
        if (custParts.length > 0) return custParts.join(', ');
      }
    }
    
    // Last resort: whatever single field exists
    return shipmentParts.join(', ') || '';
  };

  // Get location display name from new format (type:id:displayName)
  const getLocationDisplayName = (locationValue) => {
    if (!locationValue) return '';
    const parts = locationValue.split(':');
    const type = parts[0];
    const displayName = parts.slice(2).join(':'); // Everything after type:id
    
    if (type === 'other') {
      return displayName || '';
    }
    return displayName || '';
  };

  // Get location display name (legacy function for backward compatibility)
  const getLocationName = (type, locationId, otherText) => {
    if (type === 'customer_address') return getCustomerAddress();
    if (type === 'yard') return getYardName(locationId);
    if (type === 'other') return otherText;
    return '';
  };

  // Format date for display (used by UI elements)
  const formatDispatchDateTime = () => {
    const parts = [];
    if (formData.dispatch_date) {
      const [year, month, day] = formData.dispatch_date.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      parts.push(date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }));
    }
    if (formData.dispatch_time_from) {
      parts.push(formatTime12h(formData.dispatch_time_from));
      if (formData.dispatch_time_to) {
        parts.push(`- ${formatTime12h(formData.dispatch_time_to)}`);
      }
    }
    return parts.join(' ');
  };

  // Convert 24h time string to 12h format (e.g. "13:30" -> "01:30PM")
  const formatTime12h = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const ampm = h < 12 ? 'AM' : 'PM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')}${ampm}`;
  };

  // Format date as MM/DD/YY
  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year.slice(2)}`;
  };

  // Generate dispatch message in sentence format:
  // Dispatch message formats per task type
  const generateDispatchMessage = () => {
    if (!shipment) return '';
    
    const container = shipment.container_number || '';
    const size = shipment.container_size || '';
    const pin = shipment.pin || '';
    const ssl = shipment.ssl || '';
    const terminalObj = terminals?.find(t => t.id === shipment.terminal_id);
    const terminalCode = terminalObj?.code || terminalObj?.name || '';
    
    // Delivery address
    const deliveryParts = [shipment.delivery_address, shipment.delivery_city, shipment.delivery_state, shipment.delivery_zip].filter(Boolean);
    const deliveryAddr = deliveryParts.join(', ');
    
    // Location names from form selections
    const locationTo = getLocationDisplayName(formData.location_to);
    const locationFrom = getLocationDisplayName(formData.location_from);
    
    // Build date/time fragment
    let dateTimePart = '';
    if (formData.dispatch_date) {
      dateTimePart = formatDateShort(formData.dispatch_date);
      if (formData.dispatch_time_from) {
        dateTimePart += ` ${formatTime12h(formData.dispatch_time_from)}`;
        if (formData.dispatch_time_to) {
          dateTimePart += ` - ${formatTime12h(formData.dispatch_time_to)}`;
        }
      }
    }
    
    // Build requirements
    const reqs = [];
    if (formData.delivery_type === 'LIVE_UNLOAD') reqs.push('LIVE UNLOAD');
    if (formData.delivery_type === 'DROP') reqs.push('DROP');
    if (formData.require_signature) reqs.push('Signature (POD)');
    if (formData.require_chassis_number) reqs.push('Chassis#');
    if (formData.require_seal_number) reqs.push('Seal#');
    if (formData.require_container_picture) reqs.push('Container Picture');
    if (formData.require_terminal_ticket) reqs.push('Terminal Ticket');
    
    // Build customer hours string
    let customerHoursPart = '';
    if (formData.customer_hours_from) {
      customerHoursPart = `Hrs ${formatTime12h(formData.customer_hours_from)}`;
      if (formData.customer_hours_to) customerHoursPart += ` - ${formatTime12h(formData.customer_hours_to)}`;
    }
    
    const szPart = size ? `Sz ${size}` : '';
    
    let message = '';
    switch (formData.task_type) {
      // Load out container EMCU8437680 from APM400, Pin#9482, Sz 40HQ, at 04/07/26 10:30AM - 11:00AM and deliver to C Yard
      case 'LOAD_OUT_DELIVER': {
        const toLoc = locationTo || deliveryAddr || '';
        message = `Load out container ${container} from ${terminalCode}`;
        const frags = [];
        if (pin) frags.push(`Pin#${pin}`);
        if (szPart) frags.push(szPart);
        if (frags.length) message += `, ${frags.join(', ')}`;
        if (dateTimePart) message += `, at ${dateTimePart}`;
        if (toLoc) message += ` and deliver to ${toLoc}`;
        if (customerHoursPart) message += `, ${customerHoursPart}`;
        break;
      }
      // Empty out a Sz 40HQ, EG container from APM400 with Pin#9482 and deliver to C Yard
      case 'EMPTY_OUT_DELIVER': {
        const toLoc = locationTo || deliveryAddr || '';
        const sizeSsl = [szPart, ssl].filter(Boolean).join(', ');
        message = `Empty out${sizeSsl ? ` a ${sizeSsl}` : ''} container from ${terminalCode}`;
        if (pin) message += ` with Pin#${pin}`;
        if (dateTimePart) message += `, at ${dateTimePart}`;
        if (toLoc) message += ` and deliver to ${toLoc}`;
        if (customerHoursPart) message += `, ${customerHoursPart}`;
        break;
      }
      // Return empty container EMCU8437680 Sz 40HQ, from Customer to C Yard
      case 'RETURN_EMPTY': {
        message = `Return empty container ${container}`;
        if (szPart) message += ` ${szPart}`;
        if (locationFrom) message += `, from ${locationFrom}`;
        if (locationTo) message += ` to ${locationTo}`;
        if (dateTimePart) message += `, at ${dateTimePart}`;
        if (customerHoursPart) message += `, ${customerHoursPart}`;
        break;
      }
      // Return load container EMCU8437680 Sz 40HQ, from Customer to Terminal
      case 'RETURN_LOAD': {
        message = `Return load container ${container}`;
        if (szPart) message += ` ${szPart}`;
        if (locationFrom) message += `, from ${locationFrom}`;
        if (locationTo) message += ` to ${locationTo}`;
        if (dateTimePart) message += `, at ${dateTimePart}`;
        if (customerHoursPart) message += `, ${customerHoursPart}`;
        break;
      }
      // Deliver LOAD container EMCU8437680, Sz 40HQ, from C Yard, to 5491 E Philadelphia St at 04/08/26 09:15PM
      case 'DELIVER_FROM_TO': {
        const loadType = formData.is_load_or_empty === 'LOAD' ? 'LOAD' : formData.is_load_or_empty === 'EMPTY' ? 'EMPTY' : '';
        message = `Deliver${loadType ? ` ${loadType}` : ''} container ${container}`;
        if (szPart) message += `, ${szPart}`;
        if (locationFrom) message += `, from ${locationFrom}`;
        if (locationTo) message += `, to ${locationTo}`;
        if (dateTimePart) message += ` at ${dateTimePart}`;
        if (customerHoursPart) message += `, ${customerHoursPart}`;
        break;
      }
      default:
        message = container;
        break;
    }
    
    if (reqs.length > 0) message += `\nREQ: ${reqs.join(', ')}`;
    
    return message.trim();
  };

  // Copy dispatch message to clipboard
  const handleCopyMessage = () => {
    if (dispatchMessage) {
      navigator.clipboard.writeText(dispatchMessage)
        .then(() => toast.success('Dispatch message copied to clipboard'))
        .catch(() => toast.error('Failed to copy to clipboard'));
    }
  };

  // Send dispatch to driver
  const handleSendDispatch = async () => {
    if (!formData.driver_id) {
      toast.error('Please select a driver');
      return;
    }
    if (!formData.task_type) {
      toast.error('Please select a task');
      return;
    }

    setLoading(true);
    try {
      // Get destination from the new location format
      const destinationValue = getLocationDisplayName(formData.location_to);
      const originValue = getLocationDisplayName(formData.location_from);
      
      await createDispatchWithOverride((override) => api.post('/dispatches', {
        shipment_id: shipment.id,
        container_number: shipment.container_number,
        driver_id: formData.driver_id,
        phone_number: formData.phone_number,
        task: dispatchMessage, // Use generated message as task
        task_type: formData.task_type,
        destination: destinationValue,
        origin: originValue,
        dispatch_date: formData.dispatch_date,
        dispatch_time_from: formData.dispatch_time_from,
        dispatch_time_to: formData.dispatch_time_to,
        require_signature: formData.require_signature,
        require_seal_number: formData.require_seal_number,
        require_chassis_number: formData.require_chassis_number,
        require_container_picture: formData.require_container_picture,
        require_terminal_ticket: formData.require_terminal_ticket,
        delivery_type: formData.delivery_type || '',
        status_when_accepted: formData.status_when_accepted,
        status_when_declined: formData.status_when_declined,
        status_when_started: formData.status_when_started,
        status_when_done: formData.status_when_done,
        override_out_of_service: override,
      }));
      toast.success('Dispatch sent to driver');
      onSuccess?.();
    } catch (error) {
      if (error?.handled) {
        toast.info('Dispatch cancelled');
      } else {
        toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to create dispatch');
      }
    } finally {
      setLoading(false);
    }
  };

  // Render location dropdown - Two-step selection: Type first, then specific location
  const renderLocationDropdown = (prefix, currentValue, label) => {
    // Parse the location type and value from form data
    const locationType = formData[`${prefix}_type`] || '';
    const locationValue = formData[`${prefix}_value`] || '';
    const locationOther = formData[`${prefix}_other`] || '';
    
    // Get customer address using the improved resolution
    const customerAddress = getCustomerAddress();
    // iter 264d.14 — was `shipment?.customer_name || customer.name`. Now uses
    // the address `label` ("Main Warehouse", "Distribution Center") when the
    // shipment maps to a saved customer address, so the driver's dispatch
    // message shows the warehouse label instead of the entity name.
    const customerName = getDispatchLabelName();
    
    // Helper to get display name for current selection
    const getDisplayName = () => {
      if (!locationType) return '';
      if (locationType === 'customer_address') return customerAddress || customerName;
      if (locationType === 'yard') {
        const yard = yards?.find(y => y.id === locationValue);
        return yard ? `${yard.name}${yard.address ? ` - ${yard.address}` : ''}` : '';
      }
      if (locationType === 'terminal') {
        const terminal = terminals?.find(t => t.id === locationValue);
        return terminal ? `${terminal.name}${terminal.code ? ` (${terminal.code})` : ''}` : '';
      }
      if (locationType === 'other') return locationOther;
      return '';
    };
    
    return (
      <div className="space-y-3">
        {/* Step 1: Location Type Selection */}
        <div className="space-y-2">
          <Label className="text-slate-300 text-sm">Location Type</Label>
          <Select 
            value={locationType} 
            onValueChange={(v) => {
              handleChange(`${prefix}_type`, v);
              handleChange(`${prefix}_value`, '');
              handleChange(`${prefix}_other`, '');
              // Also update the combined location field for backward compatibility
              if (v === 'customer_address' && customerAddress) {
                // iter 264d.9 — prepend the warehouse/customer name to the
                // stored display string so the driver's dispatch message
                // reads e.g. "deliver to ABC Warehouse — 1234 Main St, LA"
                // instead of just the raw street address. Previously the
                // customer/warehouse name was dropped for DELIVER_FROM_TO.
                const namePart = customerName && customerName !== 'Customer' ? `${customerName} — ` : '';
                handleChange(prefix, `customer:${shipment?.customer_id || 'default'}:${namePart}${customerAddress}`);
                // Auto-populate hours and remark from customer's saved address
                if (shipment?.customer_id && customers?.length) {
                  const cust = customers.find(c => c.id === shipment.customer_id);
                  if (cust?.addresses?.length) {
                    const addr = cust.addresses.find(a => a.is_default) || cust.addresses[0];
                    if (addr?.receiving_hours_from) handleChange('customer_hours_from', addr.receiving_hours_from);
                    if (addr?.receiving_hours_to) handleChange('customer_hours_to', addr.receiving_hours_to);
                    if (addr?.remark) handleChange('customer_remark', addr.remark);
                  }
                }
              }
            }}
          >
            <SelectTrigger className="bg-slate-950 border-slate-800 text-white h-9" data-testid={`${prefix}-type-select`}>
              <SelectValue placeholder="Select location type..." />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800">
              <SelectItem value="customer_address" className="text-white">Customer Address</SelectItem>
              <SelectItem value="terminal" className="text-white">Terminal</SelectItem>
              <SelectItem value="yard" className="text-white">Yard</SelectItem>
              <SelectItem value="other" className="text-white">Other (Manual Input)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Step 2: Specific Location Selection based on Type */}
        {locationType === 'terminal' && (
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Select Terminal</Label>
            <Select 
              value={locationValue} 
              onValueChange={(v) => {
                handleChange(`${prefix}_value`, v);
                const terminal = terminals?.find(t => t.id === v);
                if (terminal) {
                  handleChange(prefix, `terminal:${v}:${terminal.name}${terminal.address ? ` - ${terminal.address}` : ''}`);
                }
              }}
            >
              <SelectTrigger className="bg-slate-950 border-slate-800 text-white h-9">
                <SelectValue placeholder="Select terminal..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
                {terminals?.filter(t => t.id).length > 0 ? terminals.filter(t => t.id).map(t => (
                  <SelectItem key={t.id} value={t.id} className="text-white">
                    {t.name || 'Terminal'} {t.code ? `(${t.code})` : ''}
                  </SelectItem>
                )) : (
                  <SelectItem value="no-terminals" disabled className="text-slate-500">
                    No terminals configured
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {locationType === 'yard' && (
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Select Yard</Label>
            <Select 
              value={locationValue} 
              onValueChange={(v) => {
                handleChange(`${prefix}_value`, v);
                const yard = yards?.find(y => y.id === v);
                if (yard) {
                  handleChange(prefix, `yard:${v}:${yard.name}${yard.address ? ` - ${yard.address}` : ''}`);
                }
              }}
            >
              <SelectTrigger className="bg-slate-950 border-slate-800 text-white h-9">
                <SelectValue placeholder="Select yard..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
                {yards?.filter(y => y.id).length > 0 ? yards.filter(y => y.id).map(y => (
                  <SelectItem key={y.id} value={y.id} className="text-white">
                    {y.name || 'Yard'}{y.address ? ` - ${y.address}` : ''}
                  </SelectItem>
                )) : (
                  <SelectItem value="no-yards" disabled className="text-slate-500">
                    No yards configured
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {locationType === 'customer_address' && (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Customer Address</Label>
              <Input
                value={customerAddress || 'No address on file'}
                disabled
                className="bg-slate-950 border-slate-800 text-slate-400 h-9 text-sm"
              />
              {!customerAddress && (
                <p className="text-xs text-amber-500">Add address in customer settings</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Receiving Hours</Label>
              <div className="flex items-center gap-2">
                <Select value={formData.customer_hours_from} onValueChange={(v) => handleChange('customer_hours_from', v)}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white h-9 w-32" data-testid="customer-hours-from">
                    <SelectValue placeholder="From" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
                    {TIME_OPTIONS.map(t => (
                      <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-slate-400 text-sm">to</span>
                <Select value={formData.customer_hours_to} onValueChange={(v) => handleChange('customer_hours_to', v)}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white h-9 w-32" data-testid="customer-hours-to">
                    <SelectValue placeholder="To" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
                    {TIME_OPTIONS.map(t => (
                      <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Address Remark</Label>
              <Input
                value={formData.customer_remark}
                onChange={(e) => handleChange('customer_remark', e.target.value)}
                placeholder="e.g. Gate code #1234, Dock 5, call on arrival..."
                className="bg-slate-950 border-slate-800 text-white h-9 text-sm"
                data-testid="customer-remark-input"
              />
            </div>
          </div>
        )}
        
        {locationType === 'other' && (
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Enter Location</Label>
            <Input
              value={locationOther}
              onChange={(e) => {
                handleChange(`${prefix}_other`, e.target.value);
                handleChange(prefix, `other::${e.target.value}`);
              }}
              placeholder="Enter location manually..."
              className="bg-slate-950 border-slate-800 text-white h-9 text-sm"
            />
          </div>
        )}
      </div>
    );
  };

  // Legacy function for backward compatibility
  const renderLocationOptions = (prefix, currentType, currentLocation, currentOther) => (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-slate-300">Location Type</Label>
        <Select value={currentType} onValueChange={(v) => handleChange(`${prefix}_type`, v)}>
          <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
            <SelectValue placeholder="Select location type..." />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="customer_address" className="text-white">Customer Address</SelectItem>
            <SelectItem value="yard" className="text-white">Yard</SelectItem>
            <SelectItem value="other" className="text-white">Other (Manual Input)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {currentType === 'yard' && (
        <div className="space-y-2">
          <Label className="text-slate-300">Select Yard</Label>
          <Select value={currentLocation} onValueChange={(v) => handleChange(`${prefix}_location`, v)}>
            <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
              <SelectValue placeholder="Select yard..." />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
              {yards?.map(y => (
                <SelectItem key={y.id} value={y.id} className="text-white">{y.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {currentType === 'customer_address' && (
        <div className="space-y-2">
          <Label className="text-slate-300">Customer Address</Label>
          <Input
            value={getCustomerAddress()}
            disabled
            className="bg-slate-950 border-slate-800 text-slate-400"
          />
        </div>
      )}
      
      {currentType === 'other' && (
        <div className="space-y-2">
          <Label className="text-slate-300">Enter Location</Label>
          <Input
            value={currentOther}
            onChange={(e) => handleChange(`${prefix}_other`, e.target.value)}
            placeholder="Enter location manually..."
            className="bg-slate-950 border-slate-800 text-white"
          />
        </div>
      )}
    </div>
  );

  if (!shipment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-slate-900 border-slate-800 max-h-[90vh] overflow-y-auto" data-testid="dispatch-modal">
        <DialogHeader className="border-b border-slate-800 pb-4">
          <DialogTitle className="font-heading text-white flex items-center justify-between">
            <span>Driver Dispatch Schedule</span>
            <div className="text-right text-sm font-normal">
              <div className="text-slate-400 font-mono">{shipment.container_number}</div>
              <div className="text-slate-500 text-xs">{shipment.container_size || ''}</div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-start bg-slate-800/50 p-1 rounded-sm">
            <TabsTrigger 
              value="schedule"
              className="rounded-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              DISPATCH SCHEDULE
            </TabsTrigger>
            <TabsTrigger 
              value="events"
              className="rounded-sm data-[state=active]:bg-slate-700 data-[state=active]:text-white"
            >
              EVENTS LOGS
            </TabsTrigger>
          </TabsList>

          <TabsContent value="schedule" className="space-y-4 mt-4">
            {/* Driver Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">DRIVER *</Label>
                <Select value={formData.driver_id} onValueChange={(v) => handleChange('driver_id', v)}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="driver-select">
                    <SelectValue placeholder="Select driver..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
                    {drivers?.map(d => (
                      <SelectItem key={d.id} value={d.id} className="text-white">
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">PHONE NUMBER</Label>
                <Input
                  value={formData.phone_number}
                  onChange={(e) => handleChange('phone_number', e.target.value)}
                  placeholder="Phone number"
                  className="bg-slate-950 border-slate-800 text-white"
                />
              </div>
            </div>

            {/* Task Selection - Clickable Buttons */}
            <div className="space-y-3">
              <Label className="text-slate-300">TASK *</Label>
              <div className="flex flex-wrap gap-2">
                {TASK_TYPES.map(t => (
                  <Button
                    key={t.value}
                    type="button"
                    variant={formData.task_type === t.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleChange('task_type', t.value)}
                    className={`
                      transition-all duration-200
                      ${formData.task_type === t.value 
                        ? 'bg-blue-600 hover:bg-blue-500 text-white border-blue-600' 
                        : 'bg-slate-950 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white hover:border-slate-600'
                      }
                    `}
                    data-testid={`task-btn-${t.value.toLowerCase()}`}
                  >
                    {t.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Task-specific options */}
            {(formData.task_type === 'LOAD_OUT_DELIVER' || formData.task_type === 'EMPTY_OUT_DELIVER') && (
              <div className="p-4 bg-slate-800/50 rounded-lg space-y-4">
                <div className="text-sm text-slate-400 mb-2">
                  Terminal: <span className="text-white font-medium">{getTerminalName(shipment.terminal_id) || 'Not set'}</span>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300 font-medium">DELIVER TO *</Label>
                  {renderLocationDropdown('location_to', formData.location_to, 'Select destination...')}
                </div>
              </div>
            )}

            {(formData.task_type === 'RETURN_EMPTY' || formData.task_type === 'RETURN_LOAD') && (
              <div className="p-4 bg-slate-800/50 rounded-lg space-y-4">
                <div className="text-sm text-slate-400 mb-2">
                  Last Status: <span className="text-white font-medium">{shipment.status}</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300 font-medium">LOCATION FROM *</Label>
                    {renderLocationDropdown('location_from', formData.location_from, 'Select origin...')}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300 font-medium">LOCATION TO *</Label>
                    {renderLocationDropdown('location_to', formData.location_to, 'Select destination...')}
                  </div>
                </div>
              </div>
            )}

            {formData.task_type === 'DELIVER_FROM_TO' && (
              <div className="p-4 bg-slate-800/50 rounded-lg space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Load or Empty?</Label>
                  <Select value={formData.is_load_or_empty} onValueChange={(v) => handleChange('is_load_or_empty', v)}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="LOAD" className="text-white">Load</SelectItem>
                      <SelectItem value="EMPTY" className="text-white">Empty</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300 font-medium">LOCATION FROM *</Label>
                    {renderLocationDropdown('location_from', formData.location_from, 'Select origin...')}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300 font-medium">LOCATION TO *</Label>
                    {renderLocationDropdown('location_to', formData.location_to, 'Select destination...')}
                  </div>
                </div>
              </div>
            )}

            {/* Dispatch Time */}
            <div className="space-y-2">
              <Label className="text-slate-300">DISPATCH DATE & TIME</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={formData.dispatch_date}
                  onChange={(e) => handleChange('dispatch_date', e.target.value)}
                  className="bg-slate-950 border-slate-800 text-white w-40"
                />
                <Select value={formData.dispatch_time_from} onValueChange={(v) => handleChange('dispatch_time_from', v)}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white w-32">
                    <SelectValue placeholder="From" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
                    {TIME_OPTIONS.map(t => (
                      <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-slate-400">TO</span>
                <Select value={formData.dispatch_time_to} onValueChange={(v) => handleChange('dispatch_time_to', v)}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white w-32">
                    <SelectValue placeholder="To" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
                    {TIME_OPTIONS.map(t => (
                      <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {shipment.appointment_date && (
                <p className="text-xs text-slate-500">
                  Appointment: {new Date(shipment.appointment_date).toLocaleDateString()} {shipment.appointment_time_from || ''}
                </p>
              )}
            </div>

            {/* Delivery Type */}
            <div className="space-y-3">
              <Label className="text-slate-300 font-semibold">Delivery Type</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={formData.delivery_type === 'LIVE_UNLOAD' ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleChange('delivery_type', formData.delivery_type === 'LIVE_UNLOAD' ? '' : 'LIVE_UNLOAD')}
                  className={`transition-all duration-200 ${formData.delivery_type === 'LIVE_UNLOAD' 
                    ? 'bg-blue-600 hover:bg-blue-500 text-white border-blue-600' 
                    : 'bg-slate-950 border-slate-700 text-blue-400 hover:bg-blue-950 hover:border-blue-800'}`}
                  data-testid="delivery-type-live-btn"
                >
                  {formData.delivery_type === 'LIVE_UNLOAD' && '✓ '}LIVE UNLOAD
                </Button>
                <Button
                  type="button"
                  variant={formData.delivery_type === 'DROP' ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleChange('delivery_type', formData.delivery_type === 'DROP' ? '' : 'DROP')}
                  className={`transition-all duration-200 ${formData.delivery_type === 'DROP' 
                    ? 'bg-blue-600 hover:bg-blue-500 text-white border-blue-600' 
                    : 'bg-slate-950 border-slate-700 text-blue-400 hover:bg-blue-950 hover:border-blue-800'}`}
                  data-testid="delivery-type-drop-btn"
                >
                  {formData.delivery_type === 'DROP' && '✓ '}DROP
                </Button>
              </div>
            </div>

            {/* Additional Required Actions - Clickable Toggle Buttons */}
            <div className="space-y-3">
              <Label className="text-slate-300 font-semibold">Additional required action(s)</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant={formData.require_signature ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleChange('require_signature', !formData.require_signature)}
                  className={`
                    transition-all duration-200
                    ${formData.require_signature 
                      ? 'bg-red-600 hover:bg-red-500 text-white border-red-600' 
                      : 'bg-slate-950 border-slate-700 text-red-400 hover:bg-red-950 hover:border-red-800'
                    }
                  `}
                  data-testid="require-signature-btn"
                >
                  {formData.require_signature && '✓ '}SIGNATURE (POD)
                </Button>
                
                <Button
                  type="button"
                  variant={formData.require_chassis_number ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleChange('require_chassis_number', !formData.require_chassis_number)}
                  className={`
                    transition-all duration-200
                    ${formData.require_chassis_number 
                      ? 'bg-red-600 hover:bg-red-500 text-white border-red-600' 
                      : 'bg-slate-950 border-slate-700 text-red-400 hover:bg-red-950 hover:border-red-800'
                    }
                  `}
                  data-testid="require-chassis-btn"
                >
                  {formData.require_chassis_number && '✓ '}CHASSIS NUMBER
                </Button>
                
                <Button
                  type="button"
                  variant={formData.require_seal_number ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleChange('require_seal_number', !formData.require_seal_number)}
                  className={`
                    transition-all duration-200
                    ${formData.require_seal_number 
                      ? 'bg-red-600 hover:bg-red-500 text-white border-red-600' 
                      : 'bg-slate-950 border-slate-700 text-red-400 hover:bg-red-950 hover:border-red-800'
                    }
                  `}
                  data-testid="require-seal-btn"
                >
                  {formData.require_seal_number && '✓ '}SEAL NUMBER
                </Button>
                
                <Button
                  type="button"
                  variant={formData.require_container_picture ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleChange('require_container_picture', !formData.require_container_picture)}
                  className={`
                    transition-all duration-200
                    ${formData.require_container_picture 
                      ? 'bg-red-600 hover:bg-red-500 text-white border-red-600' 
                      : 'bg-slate-950 border-slate-700 text-red-400 hover:bg-red-950 hover:border-red-800'
                    }
                  `}
                  data-testid="require-picture-btn"
                >
                  {formData.require_container_picture && '✓ '}CONTAINER PICTURE
                </Button>
                
                <Button
                  type="button"
                  variant={formData.require_terminal_ticket ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleChange('require_terminal_ticket', !formData.require_terminal_ticket)}
                  className={`
                    transition-all duration-200
                    ${formData.require_terminal_ticket 
                      ? 'bg-red-600 hover:bg-red-500 text-white border-red-600' 
                      : 'bg-slate-950 border-slate-700 text-red-400 hover:bg-red-950 hover:border-red-800'
                    }
                  `}
                  data-testid="require-ticket-btn"
                >
                  {formData.require_terminal_ticket && '✓ '}TERMINAL TICKET
                </Button>
              </div>
            </div>

            {/* Generated Dispatch Message */}
            {dispatchMessage && (
              <div className="space-y-2">
                <Label className="text-slate-300 font-semibold flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Generated Dispatch Message
                </Label>
                <Textarea
                  value={dispatchMessage}
                  readOnly
                  className="bg-slate-950 border-slate-800 text-white font-mono text-sm min-h-[80px]"
                  data-testid="dispatch-message"
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-slate-800">
              <Button
                type="button"
                onClick={handleCopyMessage}
                disabled={!dispatchMessage}
                variant="outline"
                className="border-slate-700"
                data-testid="copy-dispatch-btn"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Text
              </Button>
              <Button
                type="button"
                onClick={handleSendDispatch}
                disabled={loading || !formData.driver_id || !formData.task_type}
                className="bg-blue-600 hover:bg-blue-500"
                data-testid="send-dispatch-btn"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                <Send className="h-4 w-4 mr-2" />
                Send to Driver
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="border-slate-700 ml-auto"
              >
                <X className="h-4 w-4 mr-2" />
                Close
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="events" className="mt-4">
            <p className="text-slate-400 text-center py-8">No dispatch events logged yet.</p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default DispatchModal;
